/**
 * Reescrita TRANSPARENTE das URLs do T-ARC para o proxy de desenvolvimento.
 *
 * Por quê: o T-ARC (.NET) não tem CORS — todo request do app leva `Authorization` +
 * `Content-Type: application/json`, o browser faz preflight e o T-ARC responde 405 sem
 * `Access-Control-*` → NENHUMA chamada passa (docs/port/10-backend-cors-web.md §0/§3).
 * Em desenvolvimento o Vite faz proxy de `/__tarc/*` para o T-ARC (vite.config.ts), o que
 * deixa as chamadas same-origin (sem preflight, e com o header `Date` legível para o
 * relógio confiável).
 *
 * O código portado (services/Api.tsx, ApiPrestador, Sync...) continua montando as URLs
 * ABSOLUTAS de sempre (`Environment.API` = https://tirvu-container-t-arc.../api/). Em vez
 * de espalhar `urlApi()` por ele, `instalarReescritaDeRede()` (chamado no main.tsx, ANTES
 * de carregar o app) embrulha `window.fetch` e `XMLHttpRequest.prototype.open`: quando o
 * host é o do T-ARC, a URL vira `<origem da página>/__tarc/<caminho>`. Tudo o mais
 * (t-credentials, réplica, t-gestor, arquivos, APIs públicas) passa intocado — eles têm
 * CORS e a lógica de réplica do t-credentials compara HOSTS (§9.3: não pode ir para proxy).
 *
 * Como a troca acontece só na hora do envio, a lógica do app (comparação de hosts,
 * logs, mensagens) continua vendo a URL original — idêntico ao nativo.
 *
 * Quando fica ativo:
 *   - `import.meta.env.DEV` (npm run dev) e `VITE_USAR_PROXY` diferente de "false";
 *   - ou `VITE_USAR_PROXY === "true"` em qualquer build (ex.: `vite preview`, que também
 *     tem o proxy configurado, ou um deploy atrás de reverse proxy com o mesmo prefixo —
 *     docs/port/10 §9.2).
 * Variáveis em `.env.example`.
 */

/** Prefixo do proxy (mesmo valor de vite.config.ts). */
export const PREFIXO_PROXY_TARC = "/__tarc";

/** Origens do T-ARC usadas pelo app (utils/Environment.tsx: API e T_ARC_BASE). */
const ORIGENS_TARC_PADRAO = ["https://tirvu-container-t-arc.xud9uj.easypanel.host"];

function hostsTarc(): Set<string> {
  const extra = String(import.meta.env.VITE_TARC_ORIGENS ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const hosts = new Set<string>();
  for (const origem of [...ORIGENS_TARC_PADRAO, ...extra]) {
    try {
      hosts.add(new URL(origem).host.toLowerCase());
    } catch {
      // origem inválida no .env: ignora
    }
  }
  return hosts;
}

let hostsCache: Set<string> | null = null;
const hosts = () => (hostsCache ??= hostsTarc());

/** true quando a reescrita deve acontecer neste build/ambiente. */
export function proxyAtivo(): boolean {
  const v = String(import.meta.env.VITE_USAR_PROXY ?? "").trim().toLowerCase();
  if (v === "true") return true;
  if (v === "false") return false;
  return Boolean(import.meta.env.DEV);
}

/**
 * Reescreve uma URL absoluta do T-ARC para o caminho do proxy (`/__tarc/...`) quando o
 * proxy está ativo; qualquer outra URL volta igual.
 */
export function urlApi(url: string): string {
  if (!proxyAtivo() || typeof window === "undefined") return url;
  return reescrever(url) ?? url;
}

/** Devolve a URL reescrita, ou null se não for do T-ARC. */
function reescrever(url: string): string | null {
  if (!url || url.startsWith(PREFIXO_PROXY_TARC)) return null;
  let u: URL;
  try {
    u = new URL(url, window.location.href);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  if (!hosts().has(u.host.toLowerCase())) return null;
  return `${window.location.origin}${PREFIXO_PROXY_TARC}${u.pathname}${u.search}${u.hash}`;
}

let instalado = false;

/**
 * Embrulha `fetch` e `XMLHttpRequest.open` para reescrever as URLs do T-ARC.
 * Idempotente; não faz nada quando o proxy não está ativo.
 */
export function instalarReescritaDeRede(): void {
  if (instalado || typeof window === "undefined" || !proxyAtivo()) return;
  instalado = true;

  const fetchOriginal = window.fetch.bind(window);

  const fetchComProxy = async (entrada: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (typeof entrada === "string" || entrada instanceof URL) {
      const nova = reescrever(String(entrada));
      return fetchOriginal(nova ?? entrada, init);
    }
    // Request já montado: recria apontando para o proxy, preservando método, headers,
    // corpo e sinal de cancelamento.
    const nova = reescrever(entrada.url);
    if (!nova) return fetchOriginal(entrada, init);
    const metodo = (init?.method ?? entrada.method ?? "GET").toUpperCase();
    const corpo =
      init?.body !== undefined
        ? init.body
        : metodo === "GET" || metodo === "HEAD"
          ? undefined
          : await entrada.clone().arrayBuffer();
    return fetchOriginal(nova, {
      method: metodo,
      headers: init?.headers ?? entrada.headers,
      body: corpo,
      signal: init?.signal ?? entrada.signal,
      cache: init?.cache ?? entrada.cache,
      redirect: init?.redirect ?? entrada.redirect,
      credentials: init?.credentials ?? entrada.credentials,
      integrity: init?.integrity ?? entrada.integrity,
      keepalive: init?.keepalive ?? entrada.keepalive,
      referrerPolicy: init?.referrerPolicy ?? entrada.referrerPolicy,
    });
  };
  window.fetch = fetchComProxy as typeof window.fetch;

  if (typeof XMLHttpRequest !== "undefined") {
    const openOriginal = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function open(
      this: XMLHttpRequest,
      metodo: string,
      url: string | URL,
      ...resto: unknown[]
    ) {
      const nova = reescrever(String(url));
      return (openOriginal as (...a: unknown[]) => void).call(this, metodo, nova ?? url, ...resto);
    } as typeof XMLHttpRequest.prototype.open;
  }

  console.info(
    `[rede] proxy de desenvolvimento ativo: ${Array.from(hosts()).join(", ")} → ${window.location.origin}${PREFIXO_PROXY_TARC}`,
  );
}
